import { useState, useEffect } from 'react';
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import UKHeatmap from './UKHeatmap';
import PacksByICBComponent from "../src/Components/RxAI/PacksByICBComponent";
import ProductSelect from './Components/ProductSelectorComponent';
import PacksByTrustComponent from './Components/RxAI/PacksByTrustComponent';

function RxAIDashboard() {
  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState("SimAlvia 60mg/300mg capsules");
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState(null);
  const [dataSource, setDataSource] = useState("gprx"); 
  const [selectedICB, setSelectedICB] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        setProductsLoading(true);
        setProductsError(null);
        const res = await fetch(`${API_URL}/api/products`);
        if (!res.ok) throw new Error(`Products HTTP ${res.status}`);
        const data = await res.json();

        let items = [];
        if (Array.isArray(data)) items = data;
        else if (Array.isArray(data.products)) items = data.products;
        else if (Array.isArray(data.items)) items = data.items;
        else if (Array.isArray(data.results)) {
          items = data.results.map(d => d?.product || d?.name || d?.label).filter(Boolean);
        }

        const unique = Array.from(new Set(items))
          .filter(Boolean)
          .sort((a, b) => String(a).localeCompare(String(b)));

        if (isMounted) {
          setProducts(unique);
        }
      } catch (e) {
        if (isMounted) setProductsError(e.message);
      } finally {
        if (isMounted) setProductsLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    setSelectedICB(null);
  }, [product, dataSource]);

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <div style={{ flex: 1 }}>
          <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
            Product
          </label>

          <ProductSelect
            products={products}
            value={product}
            onChange={setProduct}
            disabled={productsLoading || !!productsError}
            placeholder={productsLoading ? "Loading…" : "Search or select…"}
          />

          {productsError && (
            <div style={{ color: "crimson", marginTop: 6 }}>
              Failed to load products: {productsError}
            </div>
          )}
        </div>

        {/* ✅ New Toggle */}
        <div>
          <label style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
            Data Source
          </label>

          <ToggleButtonGroup
            value={dataSource}
            exclusive
            size="small"
            onChange={(e, value) => {
              if (value !== null) setDataSource(value);
            }}
          >
            <ToggleButton value="gprx">GP Rx</ToggleButton>
            <ToggleButton value="hospital">Hospital</ToggleButton>
          </ToggleButtonGroup>
        </div>

      </div>
      <div style={styles.grid}>
        <div style={styles.heatmapCol}>
          <UKHeatmap product={product} selectedICB={selectedICB} height="700px" />
        </div>

        {dataSource == "gprx" &&
          <PacksByICBComponent
            product={product}
            title="Packs by ICB"
            subtitle={
              <span className="packsSubKey">
                <span className="packsDot packsDotLast12" /> Last 12 months vs
                <span className="packsDot packsDotPrev12" /> Previous 12 months
              </span>
            }
            apiEndpoint="/api/get_packs_by_icb_last_vs_prev_12_months"
            maxHeight={400}
            onICBSelect={(name) => {
              setSelectedICB(prev => (prev === name ? null : name));
            }}
            selectedICB={selectedICB}
          />
        }

        {dataSource == "hospital" &&
          <PacksByTrustComponent
            product={product}
            title="Packs by Trust"
            subtitle={
              <span className="packsSubKey">
                <span className="packsDot packsDotLast12" /> Last 12 months vs
                <span className="packsDot packsDotPrev12" /> Previous 12 months
              </span>
            }
            apiEndpoint="/api/get_packs_by_trust_last_vs_prev_12_months"
            maxHeight={400}
          />
        }
        
      </div>
    </div>
  );
}

const styles = {
  page: {
    padding: "20px",
    width: "100%",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "minmax(300px, 1fr) minmax(100px, 0.5fr)",
    gap: "20px",
    alignItems: "start",
  },

  heatmapCol: {
    minWidth: 0,
  },

  chartCol: {
    minWidth: 0,
  },
  topBar: {
    display: "flex",
    alignItems: "flex-end",
    gap: "20px",
    paddingBottom: "15px",
  },
};

export default RxAIDashboard;